import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import type { EventListStoryblok, EventItemStoryblok } from '../../types/storyblok';

interface EventListProps {
  blok: EventListStoryblok;
}

interface EventItemProps {
  event: EventItemStoryblok;
  isLast: boolean;
}

/**
 * Resolves link URL from Storyblok multilink field.
 */
const resolveLink = (link: EventItemStoryblok['link']): string | undefined => {
  if (!link) return undefined;
  // Use cached_url which is available on all link types
  return link.cached_url || link.url || undefined;
};

/**
 * Learn More link component with arrow icon.
 */
const LearnMoreLink: React.FC<{ href: string; className?: string }> = ({
  href,
  className = '',
}) => (
  <a
    href={href}
    className={`inline-flex items-center gap-2 text-brand-blue font-medium text-base hover:underline shrink-0 ${className}`}
  >
    Learn More
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3.33337 8H12.6667"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 3.33337L12.6667 8.00004L8 12.6667"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </a>
);

/**
 * Individual event item with image, date, title, location, and optional link.
 *
 * Desktop: horizontal layout (image left, content right), Learn More next to date
 * Mobile: vertical stack (image top, content below), Learn More at bottom
 */
const EventItem: React.FC<EventItemProps> = ({ event, isLast }) => {
  const imageSrc = event.image?.filename || undefined;
  const imageAlt = event.image?.alt || event.title;
  const linkUrl = resolveLink(event.link);

  return (
    <div {...storyblokEditable(event as unknown as SbBlokData)}>
      <div className="flex flex-col gap-6 desktop:flex-row desktop:items-center desktop:gap-14">
        {/* Event image */}
        {imageSrc && (
          <div className="shrink-0 w-full desktop:w-[320px]">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full h-[269px] desktop:h-[240px] object-cover rounded-2xl shadow-[0_4px_64px_0_rgba(13,79,160,0.16)]"
            />
          </div>
        )}

        {/* Event content */}
        <div className="flex flex-col gap-4 flex-1">
          {/* Title row with date and link (desktop only) */}
          <div className="flex items-start justify-between gap-4">
            <h3 className="font-primary font-extrabold text-[24px] leading-normal text-brand-deep">
              {event.date}
            </h3>
            {/* Desktop: Learn More next to date */}
            {linkUrl && <LearnMoreLink href={linkUrl} className="hidden desktop:inline-flex" />}
          </div>

          {/* Event details */}
          <div className="flex flex-col gap-1">
            <p className="font-secondary font-semibold text-[18px] leading-normal text-gray-700">
              {event.title}
            </p>
            <p className="font-secondary font-medium text-base leading-normal text-gray-500">
              {event.location}
            </p>
          </div>

          {/* Mobile: Learn More at bottom */}
          {linkUrl && <LearnMoreLink href={linkUrl} className="desktop:hidden py-2" />}
        </div>
      </div>

      {/* Divider - show between items, not after last */}
      {!isLast && <hr className="mt-8 border-t border-gray-200" />}
    </div>
  );
};

/**
 * EventList component displays a vertical list of upcoming events.
 *
 * Desktop: horizontal cards with image left, content right
 * Mobile: stacked cards with image on top
 */
export const EventList: React.FC<EventListProps> = ({ blok }) => {
  const events = blok.events ?? [];

  return (
    <section
      {...storyblokEditable(blok as unknown as SbBlokData)}
      className="px-4 py-16 desktop:px-20 desktop:py-[124px] max-w-[1440px] mx-auto"
    >
      {events.length > 0 && (
        <div className="flex flex-col gap-8">
          {events.map((event, index) => (
            <EventItem key={event._uid} event={event} isLast={index === events.length - 1} />
          ))}
        </div>
      )}
    </section>
  );
};
